import React, { useState, useEffect } from 'react';
import { Modal } from '@fortress-technology-solutions/fortress-component-library/Molecules_Fortress';
import {
  Typography,
  Box,
  Button,
  ButtonBase,
  Chip,
  SearchInput,
  Stack,
} from '@fortress-technology-solutions/fortress-component-library/Atoms';
import {
  formatDateDisplayLocal,
  formatTimeDisplayLocal,
} from '@fortress-technology-solutions/fortress-component-library/utils';
import type { UserOption } from '../../services/azureAuthService';

type Props = {
  open: boolean,
  users: Array<UserOption>,
  onSelectUser: (userId: string) => void,
  onClose: () => void,
  currentUserId?: string,
};

/**
 * Azure SSO User Picker Modal
 *
 * Displayed when a Fortress employee logs in with Azure SSO and has
 * multiple user accounts across different organizations.
 *
 * Allows the user to select which account they want to sign in as,
 * then confirm with the "Sign In" button.
 * Users are sorted by lastSystemAccess (most recent first).
 */
const AzureSsoUserPickerModal = ({
  open,
  users,
  onSelectUser,
  onClose,
  currentUserId,
}: Props) => {
  const [selectedUserId, setSelectedUserId] = useState(currentUserId || null);
  const [orgSearch, setOrgSearch] = useState('');

  // Sort users by lastSystemAccess (most recent first)
  const sortedUsers = [...users].sort(
    (a, b) =>
      new Date(b.lastSystemAccess || 0) - new Date(a.lastSystemAccess || 0),
  );

  const filteredUsers = orgSearch.trim()
    ? sortedUsers.filter((u) =>
        u.organizationName
          .toLowerCase()
          .includes(orgSearch.trim().toLowerCase()),
      )
    : sortedUsers;

  // Reset selection and search when modal opens
  useEffect(() => {
    setSelectedUserId(currentUserId || null);
    setOrgSearch('');
  }, [open, currentUserId]);

  const handleSignIn = () => {
    if (selectedUserId) {
      onSelectUser(selectedUserId);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Select Account"
      maxWidth="sm"
      fullWidth
      ModalProps={{
        PaperProps: {
          sx: {
            width: '100%',
            maxWidth: 650,
          },
        },
      }}
      TitleProps={{ sx: { pb: 0 } }}
      ContentProps={{ sx: { pt: '12px', pb: '20px' } }}
      hideActions
      noCloseIcon
    >
      <>
        <Typography
          variant="bodyMedium"
          color="text.secondary"
          sx={{ mb: 0.75 }}
        >
          Please select which account you would like to use.
        </Typography>

        <SearchInput
          placeholder="Search..."
          value={orgSearch}
          onChange={(e) => setOrgSearch(e.target.value)}
          fullWidth
          size="small"
          sx={{ mt: 1.5, mb: 1 }}
        />

        <Box
          sx={{
            borderTop: '1px solid',
            borderBottom: '1px solid',
            borderColor: 'divider',
            maxHeight: 560,
            overflowY: 'auto',
          }}
        >
          {filteredUsers.map((user, index) => {
            const isCurrentUser = currentUserId && user.id === currentUserId;
            const isSelected = user.id === selectedUserId;
            const isLast = index === filteredUsers.length - 1;

            return (
              <ButtonBase
                key={user.id}
                onClick={() => setSelectedUserId(user.id)}
                sx={{
                  px: 2,
                  py: 1.5,
                  width: '100%',
                  textAlign: 'left',
                  position: 'relative',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  bgcolor: isSelected
                    ? 'rgba(25, 118, 210, 0.08)'
                    : 'transparent',
                  borderBottom: isLast ? 'none' : '1px solid',
                  borderColor: 'divider',
                  transition: 'background-color 0.15s',
                  '&:hover': {
                    bgcolor: isSelected
                      ? 'rgba(25, 118, 210, 0.08)'
                      : 'action.hover',
                  },
                }}
              >
                {isCurrentUser && (
                  <Chip
                    label="Current User"
                    size="small"
                    sx={{
                      position: 'absolute',
                      top: '50%',
                      right: 12,
                      transform: 'translateY(-50%)',
                      border: 'none',
                      color: 'success.dark',
                      backgroundColor: '#e8f5e9',
                      borderRadius: 1,
                      fontSize: '0.875rem',
                      height: 20,
                      '& .MuiChip-label': { px: 1 },
                    }}
                  />
                )}

                <Typography
                  variant="bodyMedium"
                  color={isCurrentUser ? 'primary.main' : undefined}
                >
                  {user.username}
                  {user.firstName && user.lastName && (
                    <>
                      {' - '}
                      {user.firstName} {user.lastName}
                    </>
                  )}
                </Typography>

                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  sx={{ mt: 0.5, pr: isCurrentUser ? 14 : 0, width: '100%' }}
                >
                  <Typography variant="bodySmall" color="text.secondary">
                    {user.organizationName}
                  </Typography>

                  {!isCurrentUser &&
                    (user.lastSystemAccess ? (
                      <Typography variant="bodySmall" color="text.secondary">
                        Last login:{' '}
                        {formatDateDisplayLocal(user.lastSystemAccess)} at{' '}
                        {formatTimeDisplayLocal(user.lastSystemAccess)}
                      </Typography>
                    ) : (
                      <Typography variant="bodySmall" color="text.secondary">
                        Never accessed
                      </Typography>
                    ))}
                </Stack>
              </ButtonBase>
            );
          })}
        </Box>
        <Stack
          direction="row"
          justifyContent="flex-end"
          alignItems="center"
          sx={{ mt: 2, pb: 1 }}
        >
          <Stack direction="row" spacing={2} alignItems="center">
            <Button
              variant="outlined"
              onClick={onClose}
              sx={{
                minWidth: 90,
                minHeight: 34,
                borderColor: 'primary.main',
                color: 'primary.main',
                '&:hover': {
                  borderColor: 'primary.main',
                  bgcolor: 'action.hover',
                },
              }}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleSignIn}
              disabled={!selectedUserId}
              sx={{ minWidth: 90, minHeight: 34, py: 0 }}
            >
              Sign In
            </Button>
          </Stack>
        </Stack>
      </>
    </Modal>
  );
};

export default AzureSsoUserPickerModal;
